import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { PaymentIcon } from '../components/PaymentIcon';
import { 
  FaStar, 
  FaGift, 
  FaShieldAlt, 
  FaClock, 
  FaChartLine,
  FaGamepad,
  FaChevronRight,
  FaCheckCircle,
  FaCreditCard,
  FaMobileAlt,
  FaUserTie,
  FaEnvelope,
  FaLock,
  FaAward,
  FaHandshake,
  FaUsers,
  FaThumbsUp
} from 'react-icons/fa';

const offers = [
  {
    id: 'allbritishcasino',
    name: 'All British Casino',
    image: '/images/offers/allbritishcasino.webp',
    bonus: '100% up to £100',
    rating: 4.8,
    features: ['Fast Payouts', 'UK Licensed', 'Live Casino'],
    url: 'https://www.allbritishcasino.com/'
  },
  {
    id: 'partycasino',
    name: 'PartyCasino',
    image: '/images/offers/partycasino.webp',
    bonus: '200% up to £200',
    rating: 4.7,
    features: ['VIP Program', 'Mobile App', 'Tournaments'],
    url: 'https://www.partycasino.com/'
  },
  {
    id: 'plazaroyal',
    name: 'Plaza Royal',
    image: '/images/offers/plazaroyal.webp',
    bonus: '150% up to £150',
    rating: 4.6,
    features: ['Crypto Accepted', '24/7 Support', 'Exclusive Games'],
    url: 'https://www.plazaroyal.com'
  }
];

const features = [
  {
    icon: FaShieldAlt,
    title: 'Licensed & Secure',
    description: 'All casinos are UKGC licensed and use SSL encryption'
  },
  {
    icon: FaClock,
    title: 'Fast Withdrawals',
    description: 'Get your winnings quickly with instant payment options'
  },
  {
    icon: FaChartLine,
    title: 'Expert Reviews',
    description: 'In-depth analysis from our team of casino experts'
  },
  {
    icon: FaGift,
    title: 'Best Bonuses',
    description: 'Exclusive offers and promotions you won\'t find elsewhere'
  }
];

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'SpinEmerge',
  url: 'https://spinemerge.com',
  description: 'UK Casino Reviews and Offers - Find the best casino bonuses and promotions',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://spinemerge.com/search?q={search_term_string}',
    'query-input': 'required name=search_term_string'
  }
};

export const Home = () => {
  return (
    <>
      <SEO
        title="UK Casino Reviews & Best Offers 2025"
        description="Discover the best UK casino offers, bonuses, and promotions. Expert reviews, comparisons, and exclusive deals from licensed casinos."
        jsonLd={jsonLd}
      />

      {/* Hero Section with 6 Anchors */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-96 h-96 bg-primary-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
            {/* Left Side - Text Content */}
            <div className="flex-1 text-center lg:text-left">
              <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-primary-400 via-accent-400 to-primary-400 bg-clip-text text-transparent animate-pulse-slow">
                Find Your Perfect Casino
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8 text-balance">
                Unlock exclusive bonuses, read expert reviews, and compare the best UK casino offers all in one place
              </p>
              
              {/* 6 Anchor Links */}
              <div className="grid max-w-xl grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                <a
                  href="#top-offers"
                  className="group bg-dark-800/80 backdrop-blur-sm border border-gray-700 rounded-xl p-4 hover:border-primary-500 transition-all duration-300 hover:scale-105"
                >
                  <FaGift className="text-primary-400 text-2xl mb-2 mx-auto group-hover:scale-110 transition-transform" />
                  <span className="text-white font-semibold">Top Offers</span>
                </a>
                <a
                  href="#reviews"
                  className="group bg-dark-800/80 backdrop-blur-sm border border-gray-700 rounded-xl p-4 hover:border-primary-500 transition-all duration-300 hover:scale-105"
                >
                  <FaStar className="text-primary-400 text-2xl mb-2 mx-auto group-hover:scale-110 transition-transform" />
                  <span className="text-white font-semibold">Reviews</span>
                </a>
                <a
                  href="#comparison"
                  className="group bg-dark-800/80 backdrop-blur-sm border border-gray-700 rounded-xl p-4 hover:border-primary-500 transition-all duration-300 hover:scale-105"
                >
                  <FaChartLine className="text-primary-400 text-2xl mb-2 mx-auto group-hover:scale-110 transition-transform" />
                  <span className="text-white font-semibold">Compare</span>
                </a>
                <a
                  href="#features"
                  className="group bg-dark-800/80 backdrop-blur-sm border border-gray-700 rounded-xl p-4 hover:border-primary-500 transition-all duration-300 hover:scale-105"
                >
                  <FaShieldAlt className="text-primary-400 text-2xl mb-2 mx-auto group-hover:scale-110 transition-transform" />
                  <span className="text-white font-semibold">Features</span>
                </a>
                <a
                  href="#games"
                  className="group bg-dark-800/80 backdrop-blur-sm border border-gray-700 rounded-xl p-4 hover:border-primary-500 transition-all duration-300 hover:scale-105"
                >
                  <FaGamepad className="text-primary-400 text-2xl mb-2 mx-auto group-hover:scale-110 transition-transform" />
                  <span className="text-white font-semibold">Games</span>
                </a>
                <a
                  href="#faq"
                  className="group bg-dark-800/80 backdrop-blur-sm border border-gray-700 rounded-xl p-4 hover:border-primary-500 transition-all duration-300 hover:scale-105"
                >
                  <FaCheckCircle className="text-primary-400 text-2xl mb-2 mx-auto group-hover:scale-110 transition-transform" />
                  <span className="text-white font-semibold">FAQ</span>
                </a>
              </div>

              <Link
                to="/offers"
                className="inline-block bg-gradient-to-r from-primary-500 to-accent-500 text-white font-bold py-4 px-8 rounded-xl hover:from-primary-600 hover:to-accent-600 transition-all duration-200 shadow-2xl hover:scale-105 text-lg"
              >
                Explore All Offers
                <FaChevronRight className="inline-block ml-2" />
              </Link>
            </div>

            {/* Right Side - Two Cards */}
            <div className="flex-1 lg:max-w-md w-full space-y-6">
              {/* Card 1 - Slot Machine */}
              <div className="group relative overflow-hidden rounded-2xl border border-gray-700 hover:border-primary-500 transition-all duration-300 hover:shadow-2xl hover:shadow-primary-500/20">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src="/images/mainherocard1.webp"
                    alt="Jackpot Win"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="eager"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-900/90 via-dark-900/50 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex items-center space-x-2 mb-2">
                      <FaStar className="text-yellow-400" />
                      <span className="text-white font-bold text-lg">777 Jackpot</span>
                    </div>
                    <p className="text-gray-300 text-sm">Hit the big win with our top-rated casinos</p>
                  </div>
                </div>
              </div>

              {/* Card 2 - Luxury Casino */}
              <div className="group relative overflow-hidden rounded-2xl border border-gray-700 hover:border-accent-500 transition-all duration-300 hover:shadow-2xl hover:shadow-accent-500/20">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src="/images/maingerocard2.webp"
                    alt="Luxury Casino Experience"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="eager"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-900/90 via-dark-900/50 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex items-center space-x-2 mb-2">
                      <FaGift className="text-accent-400" />
                      <span className="text-white font-bold text-lg">Premium Offers</span>
                    </div>
                    <p className="text-gray-300 text-sm">Exclusive bonuses for high rollers</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Top Offers Section */}
      <section id="top-offers" className="py-20 bg-dark-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
              Top Casino Offers
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Handpicked bonuses from the most trusted UK casinos
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {offers.map((offer, index) => (
              <div
                key={offer.id}
                className="bg-dark-900 border border-gray-700 rounded-2xl overflow-hidden hover:border-primary-500 transition-all duration-300 hover:shadow-2xl hover:shadow-primary-500/20 group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="relative h-48 overflow-hidden p-8">
                  <img
                    src={offer.image}
                    alt={offer.name}
                    className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute top-4 right-4 bg-primary-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {offer.bonus}
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-bold text-white">{offer.name}</h3>
                    <div className="flex items-center space-x-1">
                      <FaStar className="text-yellow-400" />
                      <span className="text-white font-semibold">{offer.rating}</span>
                    </div>
                  </div>
                  <ul className="space-y-2 mb-6">
                    {offer.features.map((feature, i) => (
                      <li key={i} className="flex items-center text-gray-300 text-sm">
                        <FaCheckCircle className="text-primary-400 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <div className="flex gap-3">
                    <a
                      href={offer.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 bg-gradient-to-r from-primary-500 to-accent-500 text-white text-center font-semibold py-3 rounded-lg hover:from-primary-600 hover:to-accent-600 transition-all duration-200"
                    >
                      Claim Bonus
                    </a>
                    <Link
                      to={`/offers/${offer.id}`}
                      className="flex-1 bg-dark-800 border border-gray-700 text-white text-center font-semibold py-3 rounded-lg hover:border-primary-500 transition-all duration-200"
                    >
                      Review
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-dark-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
              Why Choose SpinEmerge?
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Everything you need to make informed casino decisions
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-dark-800 border border-gray-700 rounded-xl p-6 hover:border-primary-500 transition-all duration-300 hover:shadow-lg hover:shadow-primary-500/20 text-center group"
                >
                  <div className="w-16 h-16 mx-auto mb-4 bg-primary-500/20 rounded-full flex items-center justify-center group-hover:bg-primary-500/30 transition-colors">
                    <Icon className="text-primary-400 text-2xl" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400 text-sm">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Interactive Stats Section */}
      <section id="reviews" className="py-20 bg-dark-800">
        <div className="container mx-auto px-4">
          <div className="bg-gradient-to-r from-primary-500/10 to-accent-500/10 border border-primary-500/20 rounded-2xl p-12">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-5xl font-bold text-primary-400 mb-2">50+</div>
                <div className="text-gray-300 text-lg">Casinos Reviewed</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-accent-400 mb-2">£2M+</div>
                <div className="text-gray-300 text-lg">Bonus Value</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-primary-400 mb-2">100K+</div>
                <div className="text-gray-300 text-lg">Happy Players</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Games Section */}
      <section id="games" className="py-20 bg-dark-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
              Popular Game Categories
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Discover thousands of games across all categories
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {['Slots', 'Live Casino', 'Table Games', 'Jackpots', 'Video Poker', 'Scratch Cards'].map((game, index) => (
              <div
                key={index}
                className="bg-dark-800 border border-gray-700 rounded-xl p-6 hover:border-primary-500 transition-all duration-300 cursor-pointer group"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-white group-hover:text-primary-400 transition-colors">
                    {game}
                  </h3>
                  <FaGamepad className="text-primary-400 group-hover:scale-110 transition-transform" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Preview Section */}
      <section id="faq" className="py-20 bg-dark-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
              Quick answers to common questions
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {[
              {
                q: 'How do I claim a casino bonus?',
                a: 'Simply click on the offer, register with the casino, and the bonus will be automatically credited to your account or you may need to enter a promo code.'
              },
              {
                q: 'Are these casinos safe?',
                a: 'Yes, all casinos featured on SpinEmerge are licensed by the UK Gambling Commission and use advanced security measures to protect your data and funds.'
              },
              {
                q: 'How quickly can I withdraw my winnings?',
                a: 'Withdrawal times vary by casino and payment method, but most offer e-wallet withdrawals within 24 hours and bank transfers within 3-5 business days.'
              }
            ].map((faq, index) => (
              <div
                key={index}
                className="bg-dark-900 border border-gray-700 rounded-xl p-6 hover:border-primary-500 transition-colors"
              >
                <h3 className="text-white font-semibold mb-2">{faq.q}</h3>
                <p className="text-gray-400">{faq.a}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/faq"
              className="inline-block text-primary-400 hover:text-primary-300 font-semibold"
            >
              View All FAQs <FaChevronRight className="inline-block ml-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* Payment Methods Section */}
      <section className="py-20 bg-dark-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
              Secure Payment Methods
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Fast, secure, and convenient payment options available at all recommended casinos
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {['Visa', 'Mastercard', 'PayPal', 'Skrill', 'Neteller', 'Bitcoin', 'Ethereum', 'Bank Transfer'].map((method, index) => (
              <div
                key={index}
                className="bg-dark-800 border border-gray-700 rounded-xl p-6 hover:border-primary-500 transition-all duration-300 text-center group flex flex-col items-center justify-center"
              >
                <div className="text-gray-300 mb-3 group-hover:scale-110 transition-transform">
                  <PaymentIcon method={method} className="w-12 h-12" />
                </div>
                <span className="text-gray-300 text-sm font-semibold">{method}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-dark-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
              How It Works
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Get started in three simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                step: '01',
                title: 'Browse Offers',
                description: 'Explore our curated list of the best UK casino bonuses and promotions',
                icon: FaGift
              },
              {
                step: '02',
                title: 'Compare & Choose',
                description: 'Read detailed reviews and compare bonuses to find your perfect match',
                icon: FaChartLine
              },
              {
                step: '03',
                title: 'Claim & Play',
                description: 'Click through to claim your bonus and start playing with extra credit',
                icon: FaCheckCircle
              }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="relative bg-dark-900 border border-gray-700 rounded-2xl p-8 hover:border-primary-500 transition-all duration-300"
                >
                  <div className="absolute -top-6 -left-6 w-12 h-12 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {item.step}
                  </div>
                  <div className="w-16 h-16 mx-auto mb-6 bg-primary-500/20 rounded-full flex items-center justify-center">
                    <Icon className="text-primary-400 text-3xl" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4 text-center">{item.title}</h3>
                  <p className="text-gray-400 text-center">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-dark-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
              What Players Say
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Real reviews from real players
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Sarah M.',
                rating: 5,
                text: 'Found the perfect casino thanks to SpinEmerge. The bonus was exactly as described and withdrawals were super fast!',
                location: 'London, UK'
              },
              {
                name: 'James K.',
                rating: 5,
                text: 'The comparison tool helped me choose between offers. Saved me hours of research. Highly recommend!',
                location: 'Manchester, UK'
              },
              {
                name: 'Emma L.',
                rating: 5,
                text: 'Love how detailed the reviews are. Found a casino with great live dealer games and amazing customer support.',
                location: 'Birmingham, UK'
              }
            ].map((testimonial, index) => (
              <div
                key={index}
                className="bg-dark-800 border border-gray-700 rounded-xl p-6 hover:border-primary-500 transition-all duration-300"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} className="text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-300 mb-4 italic">"{testimonial.text}"</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-semibold">{testimonial.name}</p>
                    <p className="text-gray-400 text-sm">{testimonial.location}</p>
                  </div>
                  <FaUsers className="text-primary-400 text-2xl" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-20 bg-dark-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
              Trusted & Verified
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              We only recommend casinos that meet the highest standards
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                icon: FaShieldAlt,
                title: 'UKGC Licensed',
                description: 'All casinos are licensed by the UK Gambling Commission'
              },
              {
                icon: FaLock,
                title: 'SSL Encrypted',
                description: 'Bank-level security for all transactions'
              },
              {
                icon: FaAward,
                title: 'Expert Reviewed',
                description: 'Each casino tested by our team of experts'
              },
              {
                icon: FaHandshake,
                title: 'Fair Play',
                description: 'Regular audits ensure fair gaming practices'
              }
            ].map((badge, index) => {
              const Icon = badge.icon;
              return (
                <div
                  key={index}
                  className="bg-dark-900 border border-gray-700 rounded-xl p-6 text-center hover:border-primary-500 transition-all duration-300 group"
                >
                  <div className="w-16 h-16 mx-auto mb-4 bg-primary-500/20 rounded-full flex items-center justify-center group-hover:bg-primary-500/30 transition-colors">
                    <Icon className="text-primary-400 text-3xl" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{badge.title}</h3>
                  <p className="text-gray-400 text-sm">{badge.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mobile Gaming Section */}
      <section className="py-20 bg-dark-900">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                Play Anywhere, Anytime
              </h2>
              <p className="text-xl text-gray-300 mb-6">
                All recommended casinos offer fully optimized mobile experiences. Play your favorite games on the go with responsive websites and dedicated mobile apps.
              </p>
              <ul className="space-y-4 mb-8">
                {['iOS & Android Apps', 'Instant Play Browser', 'Touch-Optimized Controls', 'Mobile-Exclusive Bonuses'].map((feature, i) => (
                  <li key={i} className="flex items-center text-gray-300">
                    <FaCheckCircle className="text-primary-400 mr-3 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Link
                to="/offers"
                className="inline-block bg-gradient-to-r from-primary-500 to-accent-500 text-white font-bold py-3 px-6 rounded-lg hover:from-primary-600 hover:to-accent-600 transition-all duration-200"
              >
                Explore Mobile Casinos
                <FaChevronRight className="inline-block ml-2" />
              </Link>
            </div>
            <div className="bg-dark-800 border border-gray-700 rounded-2xl p-8 text-center">
              <FaMobileAlt className="text-primary-400 text-8xl mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-white mb-4">Mobile First</h3>
              <p className="text-gray-400">
                Experience seamless gaming on any device with our mobile-optimized casino recommendations
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* VIP Programs Section */}
      <section className="py-20 bg-dark-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
              Exclusive VIP Programs
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Unlock premium rewards and personalized service
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                tier: 'Bronze',
                benefits: ['Welcome Bonus', 'Weekly Promotions', 'Email Support'],
                color: 'from-yellow-600 to-yellow-800'
              },
              {
                tier: 'Silver',
                benefits: ['Enhanced Bonuses', 'Priority Support', 'Exclusive Tournaments'],
                color: 'from-gray-400 to-gray-600'
              },
              {
                tier: 'Gold',
                benefits: ['VIP Manager', 'Cashback Rewards', 'Luxury Gifts'],
                color: 'from-yellow-400 to-yellow-600'
              }
            ].map((program, index) => (
              <div
                key={index}
                className="bg-dark-900 border border-gray-700 rounded-2xl p-8 hover:border-primary-500 transition-all duration-300 relative overflow-hidden"
              >
                <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${program.color} opacity-20 blur-2xl`}></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-6">
                    <FaUserTie className="text-primary-400 text-4xl" />
                    <span className={`bg-gradient-to-r ${program.color} text-white px-4 py-2 rounded-full font-bold`}>
                      {program.tier}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">{program.tier} Tier</h3>
                  <ul className="space-y-3 mb-6">
                    {program.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-center text-gray-300">
                        <FaCheckCircle className="text-primary-400 mr-3 flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Responsible Gaming Section */}
      <section className="py-20 bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900">
        <div className="container mx-auto px-4">
          <div className="bg-dark-800 border border-gray-700 rounded-2xl p-12 max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <FaHandshake className="text-primary-400 text-5xl mx-auto mb-4" />
              <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
                Responsible Gaming
              </h2>
              <p className="text-xl text-gray-300">
                We promote safe and responsible gambling practices
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {[
                'Set deposit limits',
                'Take regular breaks',
                'Never chase losses',
                'Gamble only what you can afford'
              ].map((tip, i) => (
                <div key={i} className="flex items-start space-x-3 bg-dark-900 rounded-lg p-4">
                  <FaThumbsUp className="text-primary-400 text-xl flex-shrink-0 mt-1" />
                  <span className="text-gray-300">{tip}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap justify-center gap-6">
              <a
                href="https://www.gambleaware.org"
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-80 hover:opacity-100 transition-opacity"
              >
                <img
                  src="/images/footer/gambleaware.webp"
                  alt="GambleAware"
                  className="h-12 w-auto"
                  loading="lazy"
                />
              </a>
              <a
                href="https://www.gamcare.org.uk"
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-80 hover:opacity-100 transition-opacity"
              >
                <img
                  src="/images/footer/gamcare.webp"
                  alt="GamCare"
                  className="h-12 w-auto"
                  loading="lazy"
                />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-dark-800">
        <div className="container mx-auto px-4">
          <div className="bg-gradient-to-r from-primary-500/10 to-accent-500/10 border border-primary-500/20 rounded-2xl p-12 max-w-3xl mx-auto text-center">
            <FaEnvelope className="text-primary-400 text-5xl mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
              Stay Updated
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Get the latest casino offers, exclusive bonuses, and expert tips delivered to your inbox
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 rounded-lg bg-dark-900 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 transition-colors"
              />
              <button className="bg-gradient-to-r from-primary-500 to-accent-500 text-white font-bold py-4 px-8 rounded-lg hover:from-primary-600 hover:to-accent-600 transition-all duration-200 whitespace-nowrap">
                Subscribe
              </button>
            </div>
            <p className="text-gray-400 text-sm mt-4">
              We respect your privacy. Unsubscribe at any time.
            </p>
          </div>
        </div>
      </section>

      {/* Comparison CTA */}
      <section id="comparison" className="py-20 bg-gradient-to-r from-primary-500/10 to-accent-500/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            Compare Casino Offers Side-by-Side
          </h2>
          <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
            Make the best decision by comparing bonuses, features, and terms all in one place
          </p>
          <Link
            to="/comparison"
            className="inline-block bg-gradient-to-r from-primary-500 to-accent-500 text-white font-bold py-4 px-8 rounded-xl hover:from-primary-600 hover:to-accent-600 transition-all duration-200 shadow-2xl hover:scale-105 text-lg"
          >
            Compare Now
            <FaChevronRight className="inline-block ml-2" />
          </Link>
        </div>
      </section>
    </>
  );
};

